import { useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { X, Lock } from 'lucide-react';
import { Badge } from '../../ui/badge';
import type { AutomationSettings, CommunicationChannel } from '../../../types';
import { defaultChannels } from '../../../config/channels';
import { useLanguage } from '../../../contexts/LanguageContext';
import { channelIcons, ChannelSvgIcon } from '../../../utils/Constants';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { useReadOnly } from '../../../hooks/useReadOnly';
import { TabHeader } from '../../common/TabHeader';

interface AutomationsPanelProps {
  automationSettings: AutomationSettings;
  communicationChannels: CommunicationChannel[];
  onUpdateSettings: (settings: AutomationSettings) => void;
  onClose: () => void;
}

export function AutomationsTab({ 
  automationSettings, 
  communicationChannels,
  onUpdateSettings, 
  onClose 
}: AutomationsPanelProps) {
  const { t } = useLanguage();
  const readOnly = useReadOnly();
  const [settings, setSettings] = useState<AutomationSettings>(automationSettings);

  const handleWatchEnabledChange = (enabled: boolean) => {
    const newSettings = {
      ...settings,
      watch: { ...settings.watch, enabled }
    };
    setSettings(newSettings);
    onUpdateSettings(newSettings);
  };

  const handleWatchFrequencyChange = (value: string) => {
    const frequencyDays = parseInt(value) || 1;
    const periodDays = Math.min(settings.watch.periodDays, frequencyDays);
    const newSettings = {
      ...settings,
      watch: { ...settings.watch, frequencyDays, periodDays }
    };
    setSettings(newSettings);
    onUpdateSettings(newSettings);
  };

  const handleWatchPeriodChange = (value: string) => {
    const periodDays = Math.min(parseInt(value) || 1, settings.watch.frequencyDays);
    const newSettings = {
      ...settings,
      watch: { ...settings.watch, periodDays }
    };
    setSettings(newSettings);
    onUpdateSettings(newSettings);
  };

  const handleWatchPreferredHourChange = (value: string) => {
    const preferredHour = Math.max(0, Math.min(23, parseInt(value) || 0));
    const newSettings = {
      ...settings,
      watch: { ...settings.watch, preferredHour }
    };
    setSettings(newSettings);
    onUpdateSettings(newSettings);
  };

  const handleGenerationEnabledChange = (enabled: boolean) => {
    const newSettings = {
      ...settings,
      generation: { ...settings.generation, enabled }
    };
    setSettings(newSettings);
    onUpdateSettings(newSettings);
  };

  const handleGenerationChannelToggle = (channelId: string) => {
    const newChannelIds = settings.generation.channelIds.includes(channelId)
      ? settings.generation.channelIds.filter(id => id !== channelId)
      : [...settings.generation.channelIds, channelId];
    
    const newSettings = {
      ...settings,
      generation: { ...settings.generation, channelIds: newChannelIds }
    };
    setSettings(newSettings);
    onUpdateSettings(newSettings);
  };

  const handleGenerationFrequencyChange = (value: string) => {
    const frequencyDays = parseInt(value) || 1;
    const newSettings = {
      ...settings,
      generation: { ...settings.generation, frequencyDays }
    };
    setSettings(newSettings);
    onUpdateSettings(newSettings);
  };

  const handleGenerationPostsChange = (value: string) => {
    const postsPerIteration = parseInt(value) || 1;
    const newSettings = {
      ...settings,
      generation: { ...settings.generation, postsPerIteration }
    };
    setSettings(newSettings);
    onUpdateSettings(newSettings);
  };

  const handlePublicationEnabledChange = (enabled: boolean) => {
    const newSettings = {
      ...settings,
      publication: { ...settings.publication, enabled }
    };
    setSettings(newSettings);
    onUpdateSettings(newSettings);
  };

  const handlePublicationChannelToggle = (channelId: string) => {
    const newChannelIds = settings.publication.channelIds.includes(channelId)
      ? settings.publication.channelIds.filter(id => id !== channelId)
      : [...settings.publication.channelIds, channelId];
    
    const newSettings = {
      ...settings,
      publication: { ...settings.publication, channelIds: newChannelIds }
    };
    setSettings(newSettings);
    onUpdateSettings(newSettings);
  };

  const handlePublicationFrequencyChange = (value: string) => {
    const frequencyDays = parseInt(value) || 1;
    const newSettings = {
      ...settings,
      publication: { ...settings.publication, frequencyDays }
    };
    setSettings(newSettings);
    onUpdateSettings(newSettings);
  };

  const handlePublicationPostsChange = (value: string) => {
    const postsPerIteration = parseInt(value) || 1;
    const newSettings = {
      ...settings,
      publication: { ...settings.publication, postsPerIteration }
    };
    setSettings(newSettings);
    onUpdateSettings(newSettings);
  };

  const getNetworkConfig = (channelId: string) => {
    const channel = communicationChannels.find(c => c.id === channelId);
    if (!channel) return null;
    return defaultChannels.find(dc => dc.name === channel.network);
  };

  const getChannelIcon = (channel: CommunicationChannel): string => {
    // Retourne un emoji/symbole pour chaque réseau social
    return channelIcons[channel.network] || (channel.network || '?').charAt(0);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header — coque partagée V4-FRONT-HEADERS-UNIFORM */}
      <TabHeader title={t.automations.title} onClose={onClose} />

      {/* Content — TOUT le contenu d'Automations est "coming soon" en attendant
          la mise en prod des jobs scheduled (cf plan UI/UX 2026-05). On garde
          le rendu visible mais grisé + un badge central pour informer l'utilisateur. */}
      <div className="flex-1 overflow-y-auto p-6 relative">
        {/* Overlay semi-transparent qui empêche toute interaction */}
        <div
          className="absolute inset-0 z-10"
          style={{ backgroundColor: 'var(--color-background)', opacity: 0.6 }}
        />
        {/* Badge "Bientôt disponible" centré */}
        <div className="absolute inset-0 z-20 flex items-start justify-center pt-12 pointer-events-none">
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs"
            style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}
          >
            <Lock className="w-3 h-3" style={{ color: 'var(--color-text-tertiary)' }} />
            <span style={{ color: 'var(--color-text-secondary)' }}>{t.automations.comingSoon}</span>
          </div>
        </div>
        <div className="space-y-6 opacity-40 pointer-events-none">
          {/* Veille Section - ACTIVE */}
          <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 style={{ color: 'var(--color-text-primary)' }}>{t.automations.watch.title}</h3>
              <Switch
                checked={settings.watch.enabled}
                onCheckedChange={handleWatchEnabledChange}
                disabled={readOnly}
              />
            </div>

            <div 
              className={`space-y-3 transition-opacity ${
                settings.watch.enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'
              }`}
            >
              <div>
                <Label htmlFor="watch-frequency" className="mb-2 block text-xs">
                  {t.automations.watch.frequency}
                </Label>
                <Input
                  id="watch-frequency"
                  type="number"
                  min="1"
                  value={settings.watch.frequencyDays}
                  onChange={(e) => handleWatchFrequencyChange(e.target.value)}
                  disabled={readOnly || !settings.watch.enabled}
                  className="rounded-full h-9"
                />
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                  {t.automations.watch.frequencyDesc.replace('{days}', settings.watch.frequencyDays.toString())}
                </p>
              </div>

              <div>
                <Label htmlFor="watch-period" className="mb-2 block text-xs">
                  {t.automations.watch.period}
                </Label>
                <Input
                  id="watch-period"
                  type="number"
                  min="1"
                  max={settings.watch.frequencyDays}
                  value={settings.watch.periodDays}
                  onChange={(e) => handleWatchPeriodChange(e.target.value)}
                  disabled={readOnly || !settings.watch.enabled}
                  className="rounded-full h-9"
                />
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                  {t.automations.watch.periodDesc.replace('{period}', settings.watch.periodDays.toString()).replace('{max}', settings.watch.frequencyDays.toString())}
                </p>
              </div>

              <div>
                <Label htmlFor="watch-preferred-hour" className="mb-2 block text-xs">
                  {t.automations.watch.preferredHour}
                </Label>
                <Input
                  id="watch-preferred-hour"
                  type="number"
                  min="0"
                  max="23"
                  value={settings.watch.preferredHour}
                  onChange={(e) => handleWatchPreferredHourChange(e.target.value)}
                  disabled={readOnly || !settings.watch.enabled}
                  className="rounded-full h-9"
                />
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                  {(t.automations.watch.preferredHourDesc || 'Les sources seront rafraîchies automatiquement à {hour}h (UTC)').replace('{hour}', settings.watch.preferredHour.toString())}
                </p>
              </div>
            </div>
          </div>

          {/* Génération Section - COMING SOON */}
          <div className="rounded-lg p-4 relative" style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
            <div className="absolute inset-0 z-10 rounded-lg" style={{ backgroundColor: 'var(--color-background)', opacity: 0.6 }} />
            <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none rounded-lg">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs" style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
                <Lock className="w-3 h-3" style={{ color: 'var(--color-text-tertiary)' }} />
                <span style={{ color: 'var(--color-text-secondary)' }}>{t.automations.comingSoon}</span>
              </div>
            </div>
            <div className="opacity-40 pointer-events-none">
            <div className="flex items-center justify-between mb-4">
              <h3 style={{ color: 'var(--color-text-primary)' }}>{t.automations.generation.title}</h3>
              <Switch
                checked={settings.generation.enabled}
                onCheckedChange={handleGenerationEnabledChange}
              />
            </div>

            <div 
              className={`space-y-3 transition-opacity ${
                settings.generation.enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'
              }`}
            >
              <div>
                <Label htmlFor="generation-frequency" className="mb-2 block text-xs">
                  {t.automations.generation.frequency}
                </Label>
                <Input
                  id="generation-frequency"
                  type="number"
                  min="1"
                  value={settings.generation.frequencyDays}
                  onChange={(e) => handleGenerationFrequencyChange(e.target.value)}
                  disabled={!settings.generation.enabled}
                  className="rounded-full h-9"
                />
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                  {t.automations.generation.frequencyDesc.replace('{days}', settings.generation.frequencyDays.toString())}
                </p>
              </div>

              <div>
                <Label htmlFor="generation-posts" className="mb-2 block text-xs">
                  {t.automations.generation.postsCount}
                </Label>
                <Input
                  id="generation-posts"
                  type="number"
                  min="1"
                  value={settings.generation.postsPerIteration}
                  onChange={(e) => handleGenerationPostsChange(e.target.value)}
                  disabled={!settings.generation.enabled}
                  className="rounded-full h-9"
                />
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                  {t.automations.generation.postsDesc.replace('{count}', settings.generation.postsPerIteration.toString())}
                </p>
              </div>

              <div>
                <Label className="mb-2 block text-xs">
                  {t.automations.generation.channels}
                </Label>
                <div className="space-y-2">
                  {communicationChannels.map(channel => {
                    const networkConfig = getNetworkConfig(channel.id);
                    if (!networkConfig) return null;
                    const isSelected = settings.generation.channelIds.includes(channel.id);
                    return (
                      <button
                        key={channel.id}
                        onClick={() => handleGenerationChannelToggle(channel.id)}
                        disabled={!settings.generation.enabled}
                        className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all ${
                          !settings.generation.enabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
                        }`}

                        onMouseEnter={(e) => {
                          if (settings.generation.enabled) {
                            e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (settings.generation.enabled) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <ChannelSvgIcon
                          network={channel.network}
                          size={28}
                          className={!isSelected ? 'opacity-70' : ''}
                          fallbackClassName={isSelected ? `${networkConfig.color} ${networkConfig.textColor}` : 'border'}
                        />
                        <span className={`text-sm ${isSelected ? '' : 'opacity-70'}`} style={{ color: 'var(--color-text-primary)' }}>
                          {channel.customName || channel.profileName}
                        </span>
                      </button>
                    );
                  })}
                  {communicationChannels.length === 0 && (
                    <p className="text-sm italic" style={{ color: 'var(--color-text-secondary)' }}>
                      {t.automations.generation.noChannels}
                    </p>
                  )}
                </div>
              </div>
            </div>
            </div>
          </div>

          {/* Publication Section - COMING SOON */}
          <div className="rounded-lg p-4 relative" style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
            <div className="absolute inset-0 z-10 rounded-lg" style={{ backgroundColor: 'var(--color-background)', opacity: 0.6 }} />
            <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none rounded-lg">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs" style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
                <Lock className="w-3 h-3" style={{ color: 'var(--color-text-tertiary)' }} />
                <span style={{ color: 'var(--color-text-secondary)' }}>{t.automations.comingSoon}</span>
              </div>
            </div>
            <div className="opacity-40 pointer-events-none">
            <div className="flex items-center justify-between mb-4">
              <h3 style={{ color: 'var(--color-text-primary)' }}>{t.automations.publication.title}</h3>
              <Switch
                checked={settings.publication.enabled}
                onCheckedChange={handlePublicationEnabledChange}
              />
            </div>

            <div 
              className={`space-y-3 transition-opacity ${
                settings.publication.enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'
              }`}
            >
              <div>
                <Label htmlFor="publication-frequency" className="mb-2 block text-xs">
                  {t.automations.publication.frequency}
                </Label>
                <Input
                  id="publication-frequency"
                  type="number"
                  min="1"
                  value={settings.publication.frequencyDays}
                  onChange={(e) => handlePublicationFrequencyChange(e.target.value)}
                  disabled={!settings.publication.enabled}
                  className="rounded-full h-9"
                />
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                  {t.automations.publication.frequencyDesc.replace('{days}', settings.publication.frequencyDays.toString())}
                </p>
              </div>

              <div>
                <Label htmlFor="publication-posts" className="mb-2 block text-xs">
                  {t.automations.publication.postsCount}
                </Label>
                <Input
                  id="publication-posts"
                  type="number"
                  min="1"
                  value={settings.publication.postsPerIteration}
                  onChange={(e) => handlePublicationPostsChange(e.target.value)}
                  disabled={!settings.publication.enabled}
                  className="rounded-full h-9"
                />
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                  {t.automations.publication.postsDesc.replace('{count}', settings.publication.postsPerIteration.toString())}
                </p>
              </div>

              <div>
                <Label className="mb-2 block text-xs">
                  {t.automations.publication.channels}
                </Label>
                <div className="space-y-2">
                  {communicationChannels.map(channel => {
                    const networkConfig = getNetworkConfig(channel.id);
                    if (!networkConfig) return null;
                    const isSelected = settings.publication.channelIds.includes(channel.id);
                    return (
                      <button
                        key={channel.id}
                        onClick={() => handlePublicationChannelToggle(channel.id)}
                        disabled={!settings.publication.enabled}
                        className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all ${
                          !settings.publication.enabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
                        }`}

                        onMouseEnter={(e) => {
                          if (settings.publication.enabled) {
                            e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (settings.publication.enabled) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <ChannelSvgIcon
                          network={channel.network}
                          size={28}
                          className={!isSelected ? 'opacity-70' : ''}
                          fallbackClassName={isSelected ? `${networkConfig.color} ${networkConfig.textColor}` : 'border'}
                        />
                        <span className={`text-sm ${isSelected ? '' : 'opacity-70'}`} style={{ color: 'var(--color-text-primary)' }}>
                          {channel.customName || channel.profileName}
                        </span>
                      </button>
                    );
                  })}
                  {communicationChannels.length === 0 && (
                    <p className="text-sm italic" style={{ color: 'var(--color-text-secondary)' }}>
                      {t.automations.publication.noChannels}
                    </p>
                  )}
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}